/**
 * useProjectControlState Hook
 * @implements FS-002.6 - Project Control States
 * @implements FS-002.8 - Real-Time Collaboration
 * @implements FS-002.8.1 - Real-Time Synchronization
 * @implements FS-002.8.2 - JSON Patch Protocol
 * @implements TS-074 - Control State Ravioli Container
 *
 * Custom React hook that bridges the Ravioli container with Apollo GraphQL operations.
 * Provides:
 * - Control state management via Ravioli container
 * - GraphQL mutations for state transitions
 * - Real-time subscription for collaborative updates
 * - Optimistic updates with server reconciliation
 */

import { useEffect, useMemo, useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { reaction } from 'mobx';
import { gql } from '@apollo/client';
import {
  createProjectControlStateContainer,
  type ProjectControlStateInstance,
  type ControlState,
  type JSONPatchOperation,
} from '@/containers';

// ============================================
// GraphQL Definitions
// ============================================

/**
 * Fragment for project control state fields
 */
const PROJECT_CONTROL_STATE_FIELDS = gql`
  fragment ProjectControlStateFields on Project {
    id
    pmValidated
    poValidated
    controlState
  }
`;

/**
 * Fragment for JSON Patch operations
 */
const JSON_PATCH_FIELDS = gql`
  fragment JSONPatchFields on JSONPatchOperation {
    op
    path
    value
  }
`;

/**
 * Enter draft mode mutation
 * @implements FS-002.6.1 - Control State Definitions (Normal -> Draft)
 */
const ENTER_DRAFT_MODE = gql`
  ${PROJECT_CONTROL_STATE_FIELDS}
  ${JSON_PATCH_FIELDS}
  mutation EnterDraftMode($projectId: ID!) {
    enterDraftMode(projectId: $projectId) {
      project {
        ...ProjectControlStateFields
      }
      patch {
        ...JSONPatchFields
      }
    }
  }
`;

/**
 * Validate as PM mutation
 * @implements FS-002.6.1 - Control State Definitions (Draft -> Pending)
 */
const VALIDATE_AS_PM = gql`
  ${PROJECT_CONTROL_STATE_FIELDS}
  ${JSON_PATCH_FIELDS}
  mutation ValidateAsPm($projectId: ID!) {
    validateAsPm(projectId: $projectId) {
      project {
        ...ProjectControlStateFields
      }
      patch {
        ...JSONPatchFields
      }
    }
  }
`;

/**
 * Validate as PO mutation
 * @implements FS-002.6.1 - Control State Definitions (Pending -> Normal)
 */
const VALIDATE_AS_PO = gql`
  ${PROJECT_CONTROL_STATE_FIELDS}
  ${JSON_PATCH_FIELDS}
  mutation ValidateAsPo($projectId: ID!) {
    validateAsPo(projectId: $projectId) {
      project {
        ...ProjectControlStateFields
      }
      patch {
        ...JSONPatchFields
      }
    }
  }
`;

/**
 * Reject changes mutation
 * @implements FS-002.6.1 - Control State Definitions (Pending -> Draft)
 */
const REJECT_CHANGES = gql`
  ${PROJECT_CONTROL_STATE_FIELDS}
  ${JSON_PATCH_FIELDS}
  mutation RejectChanges($projectId: ID!) {
    rejectChanges(projectId: $projectId) {
      project {
        ...ProjectControlStateFields
      }
      patch {
        ...JSONPatchFields
      }
    }
  }
`;

/**
 * Discard draft changes mutation
 * @implements FS-002.6.4 - Discard Draft Changes
 */
const DISCARD_DRAFT_CHANGES = gql`
  ${PROJECT_CONTROL_STATE_FIELDS}
  ${JSON_PATCH_FIELDS}
  mutation DiscardDraftChanges($projectId: ID!) {
    discardDraftChanges(projectId: $projectId) {
      project {
        ...ProjectControlStateFields
      }
      patch {
        ...JSONPatchFields
      }
    }
  }
`;

/**
 * Project patch subscription for real-time updates
 * @implements FS-002.8 - Real-Time Collaboration
 */
const PROJECT_PATCH_SUBSCRIPTION = gql`
  ${PROJECT_CONTROL_STATE_FIELDS}
  ${JSON_PATCH_FIELDS}
  subscription ProjectPatchSubscription($projectId: ID!) {
    projectPatch(projectId: $projectId) {
      project {
        ...ProjectControlStateFields
      }
      patch {
        ...JSONPatchFields
      }
    }
  }
`;

// ============================================
// Types
// ============================================

/**
 * Mutation response shape from GraphQL
 */
interface ProjectPatchPayload {
  project: {
    id: string;
    pmValidated: boolean;
    poValidated: boolean;
    controlState: string;
  };
  patch: Array<{
    op: string;
    path: string;
    value?: unknown;
  }>;
}

/**
 * Initial project data for the hook
 */
export interface ProjectControlStateInput {
  /** Project ID */
  projectId: string;
  /** Initial PM validation state */
  pmValidated: boolean;
  /** Initial PO validation state */
  poValidated: boolean;
  /** Initial control state */
  controlState: ControlState;
}

/**
 * Hook return type
 */
export interface UseProjectControlStateResult {
  /** Current control state (NORMAL, DRAFT, PENDING) */
  controlState: ControlState;
  /** PM validation flag */
  pmValidated: boolean;
  /** PO validation flag */
  poValidated: boolean;
  /** Whether an operation is in progress */
  isLoading: boolean;
  /** Error message if last operation failed */
  error: string | null;

  // Control state predicates (computed)
  /** True when in NORMAL state */
  isNormal: boolean;
  /** True when in DRAFT state */
  isDraft: boolean;
  /** True when in PENDING state */
  isPending: boolean;

  // Action availability (based on control state)
  /** True when enterDraftMode action is available */
  canEnterDraftMode: boolean;
  /** True when validateAsPm action is available (submit for review) */
  canSubmitForReview: boolean;
  /** True when validateAsPo action is available (approve changes) */
  canApproveChanges: boolean;
  /** True when rejectChanges action is available */
  canRejectChanges: boolean;
  /** True when discardDraft action is available */
  canDiscardDraft: boolean;

  // Actions (trigger GraphQL mutations)
  /** Enter draft mode - transitions NORMAL -> DRAFT */
  enterDraftMode: () => Promise<void>;
  /** Submit for review (validateAsPm) - transitions DRAFT -> PENDING */
  submitForReview: () => Promise<void>;
  /** Approve changes (validateAsPo) - transitions PENDING -> NORMAL */
  approveChanges: () => Promise<void>;
  /** Reject changes - transitions PENDING -> DRAFT */
  rejectChanges: () => Promise<void>;
  /** Discard draft - transitions DRAFT -> NORMAL */
  discardDraft: () => Promise<void>;
}

// ============================================
// Hook Implementation
// ============================================

/**
 * Custom hook for managing project control state
 * @implements TS-074 - Control State Ravioli Container
 *
 * @param input - Initial project control state data
 * @returns Control state data, predicates, and action functions
 *
 * @example
 * ```tsx
 * const {
 *   controlState,
 *   isNormal, isDraft, isPending,
 *   canEnterDraftMode,
 *   enterDraftMode,
 *   isLoading,
 *   error
 * } = useProjectControlState({
 *   projectId: project.id,
 *   pmValidated: project.pmValidated,
 *   poValidated: project.poValidated,
 *   controlState: project.controlState,
 * });
 * ```
 */
export const useProjectControlState = (
  input: ProjectControlStateInput,
): UseProjectControlStateResult => {
  const apolloClient = useApolloClient();

  // Create container instance (memoized to prevent recreation)
  // Note: We intentionally only depend on projectId - other props are synced via useEffect below
  const container = useMemo<ProjectControlStateInstance>(
    () => {
      return createProjectControlStateContainer({
        projectId: input.projectId,
        pmValidated: input.pmValidated,
        poValidated: input.poValidated,
        controlState: input.controlState,
        isLoading: false,
        error: null,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.projectId],
  );

  // Track reactive state with useState for React re-renders
  const [state, setState] = useState(() => ({
    controlState: input.controlState,
    pmValidated: input.pmValidated,
    poValidated: input.poValidated,
    isLoading: false,
    error: null as string | null,
    controlStates: container.controlStates,
  }));

  // Sync container state changes to React state
  useEffect(() => {
    const dispose = reaction(
      () => ({
        data: { ...container.representationRef.current },
        controlStates: [...container.controlStates],
      }),
      ({ data, controlStates }) => {
        setState({
          controlState: data.controlState,
          pmValidated: data.pmValidated,
          poValidated: data.poValidated,
          isLoading: data.isLoading,
          error: data.error,
          controlStates,
        });
      },
      { fireImmediately: true },
    );

    return () => dispose();
  }, [container]);

  // Update container when input props change (e.g., from Apollo cache update)
  useEffect(() => {
    // Sync from server if the external props differ from container state
    const currentData = container.representationRef.current;
    if (
      currentData.pmValidated !== input.pmValidated ||
      currentData.poValidated !== input.poValidated ||
      currentData.controlState !== input.controlState
    ) {
      container.actions.syncFromServer({
        pmValidated: input.pmValidated,
        poValidated: input.poValidated,
        controlState: input.controlState,
      });
    }
  }, [container, input.pmValidated, input.poValidated, input.controlState]);

  // ============================================
  // GraphQL Subscription for Real-Time Updates
  // ============================================
  useEffect(() => {
    if (!input.projectId) return;

    const subscription = apolloClient
      .subscribe<{ projectPatch: ProjectPatchPayload }>({
        query: PROJECT_PATCH_SUBSCRIPTION,
        variables: { projectId: input.projectId },
      })
      .subscribe({
        next: ({ data }) => {
          if (data?.projectPatch) {
            // Apply the patch to local state
            const { patch, project } = data.projectPatch;

            // Sync the full state from server to ensure consistency
            container.actions.syncFromServer({
              pmValidated: project.pmValidated,
              poValidated: project.poValidated,
              controlState: project.controlState,
            });

            // Also apply the patch for any additional fields
            if (patch && patch.length > 0) {
              container.actions.applyPatch({
                patch: patch as JSONPatchOperation[],
              });
            }
          }
        },
        error: (err) => {
          console.error('[ProjectControlState] Subscription error:', err);
        },
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [apolloClient, container, input.projectId]);

  // ============================================
  // Action Functions (with GraphQL mutations)
  // ============================================

  /**
   * Mutation response type helper
   */
  type MutationResponse = {
    [key: string]: ProjectPatchPayload;
  };

  /**
   * Execute a GraphQL mutation and sync the result with the container
   */
  const executeMutation = useCallback(
    async (
      mutation: typeof ENTER_DRAFT_MODE,
      mutationName: string,
    ): Promise<void> => {
      try {
        const result = await apolloClient.mutate<MutationResponse>({
          mutation,
          variables: { projectId: input.projectId },
        });

        if (result.data) {
          const payload = result.data[mutationName];
          if (payload) {
            container.actions.syncFromServer({
              pmValidated: payload.project.pmValidated,
              poValidated: payload.project.poValidated,
              controlState: payload.project.controlState,
            });
          }
        }

        container.actions.completeMutation();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'An error occurred';
        container.actions.handleError(message);
        throw err;
      }
    },
    [apolloClient, container, input.projectId],
  );

  /**
   * Enter draft mode - transitions NORMAL -> DRAFT
   * @implements FS-002.6.1 - Control State Definitions (Normal -> Draft)
   */
  const enterDraftMode = useCallback(async (): Promise<void> => {
    container.actions.enterDraftMode();
    await executeMutation(ENTER_DRAFT_MODE, 'enterDraftMode');
  }, [container, executeMutation]);

  /**
   * Submit for review (validateAsPm) - transitions DRAFT -> PENDING
   * @implements FS-002.6.1 - Control State Definitions (Draft -> Pending)
   */
  const submitForReview = useCallback(async (): Promise<void> => {
    container.actions.validateAsPm();
    await executeMutation(VALIDATE_AS_PM, 'validateAsPm');
  }, [container, executeMutation]);

  /**
   * Approve changes (validateAsPo) - transitions PENDING -> NORMAL
   * @implements FS-002.6.1 - Control State Definitions (Pending -> Normal)
   */
  const approveChanges = useCallback(async (): Promise<void> => {
    container.actions.validateAsPo();
    await executeMutation(VALIDATE_AS_PO, 'validateAsPo');
  }, [container, executeMutation]);

  /**
   * Reject changes - transitions PENDING -> DRAFT
   * @implements FS-002.6.1 - Control State Definitions (Pending -> Draft)
   */
  const rejectChanges = useCallback(async (): Promise<void> => {
    container.actions.rejectChanges();
    await executeMutation(REJECT_CHANGES, 'rejectChanges');
  }, [container, executeMutation]);

  /**
   * Discard draft - transitions DRAFT -> NORMAL
   * @implements FS-002.6.4 - Discard Draft Changes
   */
  const discardDraft = useCallback(async (): Promise<void> => {
    container.actions.discardDraft();
    await executeMutation(DISCARD_DRAFT_CHANGES, 'discardDraftChanges');
  }, [container, executeMutation]);

  // ============================================
  // Computed Values
  // ============================================

  const isNormal = state.controlStates.includes('isNormal');
  const isDraft = state.controlStates.includes('isDraft');
  const isPending = state.controlStates.includes('isPending');

  return {
    // State values
    controlState: state.controlState,
    pmValidated: state.pmValidated,
    poValidated: state.poValidated,
    isLoading: state.isLoading,
    error: state.error,

    // Control state predicates
    isNormal,
    isDraft,
    isPending,

    // Action availability (based on control state, not loading)
    canEnterDraftMode: isNormal && !state.isLoading,
    canSubmitForReview: isDraft && !state.isLoading,
    canApproveChanges: isPending && !state.isLoading,
    canRejectChanges: isPending && !state.isLoading,
    canDiscardDraft: isDraft && !state.isLoading,

    // Actions
    enterDraftMode,
    submitForReview,
    approveChanges,
    rejectChanges,
    discardDraft,
  };
};
